/*
 * if the position is TOP and the distance between the top margin and the top of the screen
 * is less than SCREEN_GAP we will change the position to bottom
 */
import type {
  ContextMenuCoordinates,
  ContextMenuTarget,
  GenericCoordinates,
  GenericPosition,
  GenericSize
} from 'types';

const SCREEN_GAP = 80;
/*
 * This GAP is the distance between the right border to where we need to render the center of an arrow
 * to be perfect aligned to design
 */
const ARROW_GAP = 30;

function getXYPosition(
  position: GenericPosition,
  target: ContextMenuTarget,
  menu: GenericSize,
  arrow: GenericSize
): GenericCoordinates {
  const targetCenter = target.left + target.width / 2;
  const x = targetCenter - menu.width + ARROW_GAP;

  // safari returns a value for `body` tag only. remaining browsers work on `html` tag.
  // @ts-ignore - tags will always be found
  const scrollTop = document.querySelector('html').scrollTop || document.querySelector('body').scrollTop;

  if (position === 'top') {
    return {
      y: (target.top - menu.height) - (arrow.height / 2) + scrollTop,
      x
    };
  }

  return {
    y: (target.top + target.height) + (arrow.height / 2) + scrollTop,
    x
  };
}

export function calculateCoordinatesWithPosition(
  target: HTMLElement,
  menu: HTMLElement | undefined | null,
  arrow: HTMLElement | undefined | null,
  currentPosition: GenericPosition
): ContextMenuCoordinates | undefined | null {
  if (!menu || !arrow) {
    return null;
  }

  const {
    top: targetTop,
    left: targetLeft,
    width: targetWidth,
    height: targetHeight
  } = target.getBoundingClientRect();
  const {
    width: menuWidth,
    height: menuHeight
  } = menu.getBoundingClientRect();
  const {
    height: arrowHeight,
    width: arrowWidth
  } = arrow.getBoundingClientRect();

  let finalPosition;

  if (currentPosition === 'top') {
    if (targetTop - menuHeight <= SCREEN_GAP) {
      finalPosition = 'bottom';
    }
  } else { // position === 'bottom'
    let clientHeight;

    if (document.documentElement) {
      clientHeight = document.documentElement.clientHeight;
    } else {
      clientHeight = 0;
    }

    const viewPortHeight = Math.max(clientHeight, window.innerHeight || 0);

    if (targetTop + targetHeight + menuHeight >= viewPortHeight - SCREEN_GAP) {
      finalPosition = 'top';
    }
  }

  if (!finalPosition) {
    finalPosition = currentPosition;
  }

  const positionsXY = getXYPosition(finalPosition, {
    top: targetTop,
    left: targetLeft,
    width: targetWidth,
    height: targetHeight
  }, {
    width: menuWidth,
    height: menuHeight
  }, {
    width: arrowWidth,
    height: arrowHeight
  });

  return {
    ...positionsXY,
    position: finalPosition
  };
}
